import React, { useMemo, useCallback, useContext } from 'react';
import { TimePicker } from 'antd';
import type { TimePickerProps as AntTimePickerProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { parseChangeAction, createCommand } from '../../base/actionParser';
import { useFieldValidation } from '../../base/useFieldValidation';
import { ValidateHelp } from '../../base/ValidateHelp';
import type { BaseComponentProps, DataSetConfig, ValidationDisplayConfig } from '../../types/common';
import { DataSetInheritContext } from '../../base/DataSetInheritContext';

/**
 * 校验规则接口
 */
export interface ValidationRule {
  type?: string;
  required?: boolean;
  message?: string;
  pattern?: string | RegExp;
  min?: number;
  max?: number;
}

export interface AndTimePickerProps extends Omit<AntTimePickerProps, 'onChange' | 'value'>, BaseComponentProps, DataSetConfig, ValidationDisplayConfig {
  changeAction?: string;
  valueFormat?: string;
  onChange?: (time: Dayjs | null, timeString: string | null) => void;
  value?: string | Dayjs;
  
  // 校验相关
  rules?: ValidationRule[];
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
}

export const AndTimePicker: React.FC<AndTimePickerProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  changeAction,
  valueFormat = 'HH:mm:ss',
  onChange,
  value: propValue,
  defaultValue,
  rules,
  validateOnChange,
  validateOnBlur: _validateOnBlur,
  showValidateHelp = true,
  ...restProps
}) => {
  const ctx = useContext(DataSetInheritContext);
  const addCommand = useAndStore((state) => state.addCommand);
  const getDataSet = useAndStore((state) => state.getDataSet);
  const setDataSet = useAndStore((state) => state.setDataSet);
  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );

  const storeValue = useMemo(() => {
    if (dataSetKey && dataSetField) return getDataSet(dataSetKey, dataSetField);
    return undefined;
  }, [dataSetKey, dataSetField, getDataSet]);

  const { validate, help } = useFieldValidation({
    rules,
    dataSet,
    dataSetField,
    sessionId,
    id,
    develop,
  });

  const parseTimeValue = useCallback((val: any): Dayjs | null => {
    if (!val) return null;
    if (dayjs.isDayjs(val)) return val;
    if (typeof val === 'string') {
      const parsed = dayjs(val, valueFormat);
      return parsed.isValid() ? parsed : null;
    }
    return null;
  }, [valueFormat]);

  const displayValue = useMemo(() => {
    const rawValue = storeValue ?? propValue ?? defaultValue;
    return parseTimeValue(rawValue);
  }, [storeValue, propValue, defaultValue, parseTimeValue]);

  const handleChange = useCallback((time: Dayjs | null, timeString: string | null) => {
    if (onChange) { onChange(time, timeString); return; }
    const stringValue = timeString || '';
    if (dataSetKey && dataSetField) setDataSet(dataSetKey, dataSetField, stringValue);
    
    // 触发变更校验
    if (validateOnChange) {
      validate(stringValue);
    }
    
    if (changeAction && sessionId) {
      const { actionName, arg } = parseChangeAction(changeAction, stringValue, { id, dataSet, dataSetField }, ctx);
      addCommand(sessionId, createCommand(actionName, arg));
    }
  }, [onChange, dataSetKey, dataSetField, setDataSet, validateOnChange, validate, changeAction, sessionId, id, addCommand, ctx]);

  const handleBlur = useCallback(() => {
    if (_validateOnBlur) {
      validate(displayValue ? displayValue.format(valueFormat) : '');
    }
  }, [_validateOnBlur, validate, displayValue, valueFormat]);

  const status = help ? 'error' : undefined;

  return (
    <>
      <TimePicker
        {...restProps}
        disabled={develop ? false : restProps.disabled}
        open={develop ? false : undefined}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        style={{ width: '100%', ...restProps.style }}
        status={status}
      />
      <ValidateHelp help={help} showValidateHelp={showValidateHelp} />
    </>
  );
};

export default AndTimePicker;
